// src/utils/networkMonitor.ts

/**
 * Network connectivity monitor.
 *
 * Tracks the browser's online/offline state (navigator.onLine) so that:
 *   1. We emit first-class, queryable analytics events when the device drops or
 *      regains connectivity. Previously this signal only existed inside PostHog
 *      session replays ("Browser went offline" / "returned online"), which are
 *      not queryable or alertable.
 *   2. Streamed-request failures can be annotated with the connectivity context
 *      at the moment they failed (see getNetworkDiagnostics). That is what lets
 *      us tell "the user's connection dropped" apart from "our service failed".
 *
 * Why this exists: the dominant class of streamed "connection failed" errors is
 * a short client-side network drop mid-response. It is invisible to the backend
 * and load balancer (the request often still completes server-side), so the only
 * place it can be observed is here, in the browser.
 */

import { track } from "./analytics";

interface NetworkState {
  online: boolean;
  /** epoch ms of the most recent offline transition, or null if never offline */
  lastOfflineAt: number | null;
  /** epoch ms of the most recent online transition, or null if never fired */
  lastOnlineAt: number | null;
  /** number of offline transitions during this page session */
  offlineCount: number;
}

const state: NetworkState = {
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  lastOfflineAt: null,
  lastOnlineAt: null,
  offlineCount: 0,
};

/** Window (ms) within which a prior offline event is considered "recent". */
const RECENTLY_OFFLINE_WINDOW_MS = 30_000;

let initialized = false;

function handleOffline(): void {
  state.online = false;
  state.lastOfflineAt = Date.now();
  state.offlineCount += 1;
  track("app:went_offline", {
    offline_count: state.offlineCount,
  });
}

function handleOnline(): void {
  const now = Date.now();
  const offlineDurationMs = state.lastOfflineAt !== null ? now - state.lastOfflineAt : null;
  state.online = true;
  state.lastOnlineAt = now;
  track("app:back_online", {
    offline_count: state.offlineCount,
    offline_duration_ms: offlineDurationMs,
  });
}

/**
 * Register the online/offline listeners. Safe to call more than once (idempotent).
 * Call once at app startup, alongside initPostHog().
 */
export function initNetworkMonitor(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);
}

export interface NetworkDiagnostics {
  /** navigator.onLine at snapshot time. false is a strong "user's connection is down" signal. */
  network_online: boolean;
  /** ms since the last offline transition, or null if the device never went offline this session. */
  ms_since_last_offline: number | null;
  /** true if the device went offline within the last RECENTLY_OFFLINE_WINDOW_MS. */
  recently_offline: boolean;
  /** number of offline transitions during this page session. */
  offline_count: number;
}

/**
 * Snapshot of the current connectivity context, for attaching to error events.
 * Pure and side-effect free - safe to call from any error path.
 */
export function getNetworkDiagnostics(): NetworkDiagnostics {
  const online = typeof navigator !== "undefined" ? navigator.onLine : state.online;
  const msSinceLastOffline = state.lastOfflineAt !== null ? Date.now() - state.lastOfflineAt : null;
  return {
    network_online: online,
    ms_since_last_offline: msSinceLastOffline,
    recently_offline:
      msSinceLastOffline !== null && msSinceLastOffline <= RECENTLY_OFFLINE_WINDOW_MS,
    offline_count: state.offlineCount,
  };
}

/**
 * Test-only: reset module state and listener registration.
 * Not exported from any barrel; intended for unit tests.
 */
export function __resetNetworkMonitorForTests(): void {
  if (typeof window !== "undefined") {
    window.removeEventListener("offline", handleOffline);
    window.removeEventListener("online", handleOnline);
  }
  state.online = typeof navigator !== "undefined" ? navigator.onLine : true;
  state.lastOfflineAt = null;
  state.lastOnlineAt = null;
  state.offlineCount = 0;
  initialized = false;
}
